import { createRouter } from '@tanstack/react-router';
import { setupRouterSsrQueryIntegration } from '@tanstack/react-router-ssr-query';
import { NuqsAdapter } from 'nuqs/adapters/tanstack-router';
import * as TanstackQuery from './integrations/tanstack-query/root-provider';
import { deLocalizeUrl, localizeUrl } from '@/locale/paraglide/runtime';
import { Routes } from '@/lib/routes';
import { routeTree } from './routeTree.gen';

/**
 * TanStack Router instance
 * https://github.com/TanStack/router/blob/main/examples/react/start-basic-cloudflare/src/router.tsx
 * https://github.com/backpine/tanstack-start-on-cloudflare/blob/main/src/router.tsx
 * https://tanstack.com/router/latest/docs/guide/internationalization-i18n#url-localization-via-router-rewrite
 */
export const getRouter = () => {
  const queryContext = TanstackQuery.getContext();

  const router = createRouter({
    routeTree,
    context: { ...queryContext },
    // URL Localization via Router Rewrite
    rewrite: {
      input: ({ url }) => deLocalizeUrl(url),
      output: ({ url }) => localizeUrl(url),
    },
    defaultPreload: 'intent',
    scrollRestoration: true,
    scrollToTopSelectors: ['[data-scroll-restoration-id="app-content"]'],
    // Only animate gallery <-> prompt-detail, prompt-detail -> generate,
    // and history <-> creation-detail, creation-detail -> generate.
    // Filter tweaks, hash jumps, and unrelated routes stay instant.
    defaultViewTransition: {
      types: ({ fromLocation, toLocation, pathChanged, hashChanged }) => {
        if (!pathChanged) return false;
        if (hashChanged && !pathChanged) return false;
        const from = fromLocation?.pathname ?? '';
        const to = toLocation.pathname;
        // Prompt browsing flow
        const isGalleryToDetail = from === '/' && to.startsWith('/prompt/');
        const isDetailToGallery = from.startsWith('/prompt/') && to === '/';
        const isFavoritesToPromptDetail =
          from === '/favorites' && to.startsWith('/prompt/');
        const isPromptDetailToFavorites =
          from.startsWith('/prompt/') && to === '/favorites';
        const isDetailToGenerate =
          from.startsWith('/prompt/') && to === Routes.Generate;
        const isGenerateToPromptDetail =
          from === Routes.Generate && to.startsWith('/prompt/');
        if (
          isGalleryToDetail ||
          isDetailToGallery ||
          isFavoritesToPromptDetail ||
          isPromptDetailToFavorites
        )
          return ['prompt-detail'];
        if (isDetailToGenerate || isGenerateToPromptDetail)
          return ['prompt-generate'];
        // Creation browsing flow
        const isHistoryToCreation =
          from === '/history' && to.startsWith('/creation/');
        const isCreationToHistory =
          from.startsWith('/creation/') && to === '/history';
        const isFavoritesToCreationDetail =
          from === '/favorites' && to.startsWith('/creation/');
        const isCreationDetailToFavorites =
          from.startsWith('/creation/') && to === '/favorites';
        const isCreationToGenerate =
          from.startsWith('/creation/') && to === Routes.Generate;
        const isGenerateToCreationDetail =
          from === Routes.Generate && to.startsWith('/creation/');
        if (
          isHistoryToCreation ||
          isCreationToHistory ||
          isFavoritesToCreationDetail ||
          isCreationDetailToFavorites
        )
          return ['creation-detail'];
        if (isCreationToGenerate || isGenerateToCreationDetail)
          return ['creation-generate'];
        return false;
      },
    },
    // Wrap: provides NuqsAdapter and TanstackQuery.Provider for SSR
    // These providers wrap the entire route tree content (not RootDocument)
    // RootDocument (shellComponent)
    // └─ Wrap (NuqsAdapter + TanstackQuery.Provider)
    //     └─ Route tree content (RootComponent / errorComponent / notFoundComponent)
    Wrap: (props: { children: React.ReactNode }) => {
      return (
        <NuqsAdapter>
          <TanstackQuery.Provider {...queryContext}>
            {props.children}
          </TanstackQuery.Provider>
        </NuqsAdapter>
      );
    },
  });

  setupRouterSsrQueryIntegration({
    router,
    queryClient: queryContext.queryClient,
  });

  return router;
};
